"use client";

import { EmblaOptionsType } from "embla-carousel";
import type { AutoplayOptionsType } from "embla-carousel-autoplay";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { JSX, useEffect, useState } from "react";
import { NextButton, PrevButton, usePrevNextButtons } from "./DotButtons";
import { DotButton, useDotButton } from "./SquareButtons";
import "./embla.css";

type CarouselProps = {
  items: string[] | React.ReactNode[] | JSX.Element[];
  options?: EmblaOptionsType;
  fullSize?: boolean;
  autoPlay?: boolean;
  autoPlayOptions?: AutoplayOptionsType;
  bgColor?: string;
};

const Carousel = ({ ...props }: CarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    props.options,
    props.autoPlay ? [Autoplay(props.autoPlayOptions)] : [],
  );
  const [width, setWidth] = useState(0); // Initialize with 0 to avoid SSR window error
  const [snaps, setSnaps] = useState(0);

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  useEffect(() => {
    setWidth(window.innerWidth); // Set width on mount (client only)
    const handleResize = () => {
      setWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (width < 550) {
      setSnaps(1);
    }
    if (width > 550 && width < 670) {
      setSnaps(2);
    }
    if (width >= 670 && width < 850) {
      setSnaps(3);
    }
    if (width >= 850) {
      setSnaps(4);
    }
  }, [width, snaps]);

  // min-[850px]:basis-1/4

  return (
    <section
      className={`z-10 m-auto w-full ${props.fullSize ? "max-w-full" : "max-w-[70rem]"}`}
    >
      <div className="overflow-hidden" ref={emblaRef}>
        <div
          className={`touch-pan-y-pinch-zoom -ml-4 flex backface-hidden ${!props.fullSize ? "min-[670px]:-ml-6 min-[850px]:-ml-8" : ""}`}
        >
          {props.items.map((item, i) => (
            <div
              className={`transform-gpu ${!props.bgColor ? "bg-background" : props.bgColor} flex min-w-0 flex-shrink-0 flex-grow-0 basis-full items-stretch pl-4 ${!props.fullSize ? "min-[550px]:basis-1/2 min-[670px]:basis-1/3 min-[670px]:pl-8 lg:basis-1/5" : ""}`}
              key={i}
            >
              {item}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-center">
        {!props.fullSize && (
          <div className="flex items-center gap-[0.6rem]">
            <PrevButton
              onClick={() => onPrevButtonClick(snaps)}
              disabled={prevBtnDisabled}
            />
            <NextButton
              onClick={() => onNextButtonClick(snaps)}
              disabled={nextBtnDisabled}
            />
          </div>
        )}

        {props.fullSize && (
          <div className="flex-end mr-[-0.6rem] flex flex-wrap items-center">
            {scrollSnaps.map((_, index: number) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                onMouseDown={(event) => event.preventDefault()}
                className={`gs--carousel-dot ${
                  index === selectedIndex ? "gs--carousel-dot--selected" : ""
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Carousel;
